import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import Button from "../../../components/ui/Button";
import BackButton from "../../../components/ui/BackButton";
import EmployeeDetailsTimeline from "./EmployeeDetailsTimeline";
import SelfRatingsOKRs from "./SelfRatingsOKRs";
import TeamRatingsFeedbacks from "./TeamRatingsFeedbacks";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { appraisalAdminService } from "../../../services/appraisalAdminService";
import { authService } from "../../../services";
import type { EmployeeDetailsData } from "../../../services/appraisalAdminService";
import ErrorState from "../../../components/ui/ErrorState";
import { deriveErrorState } from "../../../utils/errorStateUtils";
import TeamRating from "../../../assets/team-rating.svg";
import EmployeeTimeline from "../../../assets/empoyee-timeline.svg";
import OkrReview from "../../../assets/okr-review.svg";

type TimelineEvent = {
  id: string;
  title: string;
  when: string; // human readable date/time
  status?: string;
  note?: string;
  actionBy?: string;
  relationshipType?: string;
};

type EmployeeInfo = {
  employeeId: string;
  name: string;
  manager?: string;
  department?: string;
  designation?: string;
  managerRating?: string;
  feedbackRating?: string;
  overallRating?: string;
  employeeManager?: string;
  hodRating?: string;
  hrRating?: string;
  adminStatus?: string; // 'pending' | 'submitted'
};

interface Props {
  employee?: EmployeeInfo;
  timeline?: TimelineEvent[];
}

const AppraisalAdminViewDetails: React.FC<Props> = ({ employee, timeline }) => {
  const location = useLocation();
  const params = useParams();
  
  // State for API data
  const [employeeDetailsData, setEmployeeDetailsData] = useState<EmployeeDetailsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("details");
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);

  const navState = (location.state || {}) as {
    employee?: EmployeeInfo;
    timeline?: TimelineEvent[];
  };

  const selectedEmployeeId =
    params.employeeId ||
    navState.employee?.employeeId ||
    employee?.employeeId ||
    null;
  const loggedInEmail = authService.getStoredUser()?.email ?? null;

  // Fetch employee details from API
  const fetchEmployeeDetails = useCallback(async () => {
    if (!loggedInEmail) {
      setEmployeeDetailsData(null);
      setError("User email not available");
      setErrorStatus(undefined);
      setIsLoading(false);
      return;
    }

    if (!selectedEmployeeId) {
      setEmployeeDetailsData(null);
      setError("Employee ID not provided");
      setErrorStatus(undefined);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setErrorStatus(undefined);

      const targetEmployeeId = selectedEmployeeId as string;
      const response = await appraisalAdminService.getEmployeeDetails(loggedInEmail, targetEmployeeId);

      if (response.data) {
        setEmployeeDetailsData(response.data);
      } else {
        setEmployeeDetailsData(null);
        setError("No employee details found");
      }
    } catch (err) {
      console.error("Failed to fetch employee details:", err);
      setEmployeeDetailsData(null);
      let status: number | undefined;
      let message = "Failed to load employee details. Please try again.";

      if (err && typeof err === 'object') {
        if ('status_code' in err && typeof (err as { status_code?: unknown }).status_code === 'number') {
          status = (err as { status_code: number }).status_code;
        }
        if ('message' in err && typeof (err as { message?: unknown }).message === 'string') {
          message = (err as { message: string }).message;
        }
      } else if (err instanceof Error && err.message) {
        message = err.message;
      }

      setErrorStatus(status);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [loggedInEmail, selectedEmployeeId]);

  useEffect(() => {
    fetchEmployeeDetails();
  }, [fetchEmployeeDetails]);

  // Helper function to retry API call
  const handleRetry = () => {
    if (!isLoading) {
      fetchEmployeeDetails();
    }
  };

  // Rating labels mapping
  const ratingLabels: Record<number, string> = {
    1: "1-Excel",
    2: "2-Fully Met",
    3: "3-Mostly Met",
    4: "4-Requires Action",
  };

  // Helper function to format rating (remove .0 for whole numbers)
  const formatRatingValue = (value: number | null): string => {
    if (value === null) return "N/A";
    // If it's a whole number, return without decimal
    return value % 1 === 0 ? `${value}` : `${value}`;
  };

  // Helper function to format rating with label (e.g., "4-Requires Action / 4")
  const formatRatingWithLabel = (value: number | null | string): string => {
    if (value === null) return "N/A";
    
    let numValue: number;
    
    // If it's a string that contains a label (like "1-Excel"), extract just the number
    if (typeof value === 'string' && value.includes('-')) {
      const parts = value.split('-');
      numValue = parseFloat(parts[0]);
    } else {
      numValue = typeof value === 'string' ? parseFloat(value) : value;
    }
    
    if (isNaN(numValue)) return "N/A";
    
    const roundedValue = Math.round(numValue);
    const label = ratingLabels[roundedValue];
    const numericValue = formatRatingValue(numValue);
    
    if (label) {
      return `${label} / ${numericValue}`;
    }
    // Fallback to numeric format
    return `${numericValue}/4`;
  };

  // Transform API data to component format
  const getTransformedEmployeeData = (): EmployeeInfo => {
    if (employeeDetailsData?.employee_info) {
      const empInfo = employeeDetailsData.employee_info;
      return {
        employeeId: empInfo.emp_id,
        name: empInfo.emp_name,
        manager: empInfo.emp_manager,
        department: empInfo.emp_department,
        designation: empInfo.emp_designation,
        managerRating: empInfo.emp_manager_rating ? formatRatingWithLabel(empInfo.emp_manager_rating) : "N/A",
        feedbackRating: empInfo.emp_feedback_rating ? formatRatingWithLabel(empInfo.emp_feedback_rating) : "N/A",
        overallRating: empInfo.emp_overall_rating ? formatRatingWithLabel(empInfo.emp_overall_rating) : "N/A",
        employeeManager: empInfo.emp_manager,
        hodRating: empInfo.emp_hod_rating ? formatRatingWithLabel(empInfo.emp_hod_rating) : "N/A",
        hrRating: empInfo.emp_hr_rating ? formatRatingWithLabel(empInfo.emp_hr_rating) : undefined,
        adminStatus: empInfo.admin_status, // 'pending' or 'submitted'
      };
    }
    
    // Fallback to passed props or default
    return employee ?? {
      employeeId: params.employeeId ?? "000000",
      name: "Jane Doe",
      manager: "John Doe (000218)",
      department: "Digital Solutions",
      designation: "Assistant Manager",
      managerRating: "0.0/4",
      feedbackRating: "0.0/4",
      overallRating: "0.0/4",
      employeeManager: "John Doe (000218)",
    };
  };

  const getTransformedTimelineData = (): TimelineEvent[] => {
    if (employeeDetailsData?.employee_timeline) {
      return employeeDetailsData.employee_timeline.map((stage, index) => ({
        id: index.toString(),
        title: stage.stage_label,
        when: stage.stage_timestamp || "Date not available",
        status: stage.stage_status,
        note: stage.remarks?.trim() ? stage.remarks.trim() : undefined,
        actionBy: stage.action_by?.trim() ? stage.action_by.trim() : undefined,
        relationshipType: stage.relationship_type?.trim() ? stage.relationship_type.trim() : undefined,
      }));
    }
    
    if (timeline && timeline.length > 0) {
      return timeline;
    }

    if (navState.timeline && navState.timeline.length > 0) {
      return navState.timeline;
    }

    return [];
  };

  const effectiveEmployee = getTransformedEmployeeData();
  const effectiveTimeline = getTransformedTimelineData();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingSpinner className="mx-auto mb-4" />
          <p className="text-gray-600">Loading employee details...</p>
        </div>
      </div>
    );
  }

  if (error && !employeeDetailsData) {
    const { type, title, message } = deriveErrorState(error, {
      fallbackMessage: error,
      statusCode: errorStatus,
    });
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <ErrorState
          type={type}
          title={title}
          message={message}
          onRetry={handleRetry}
          retryLabel={isLoading ? 'Retrying...' : 'Try Again'}
          showRetryButton
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 ">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4  text-color-primary-800 ">
          <BackButton label="Back to Employee List" />
        </div>
      </div>

      {/* Tabs */}
      <div className="responsive-tabs flex gap-2">
        <Button
          variant="ghost"
          onClick={() => setActiveTab("details")}
          className={`px-6 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
            activeTab === "details"
              ? "bg-purple-100 text-purple-800 shadow-md outline outline-2 outline-purple-800"
              : "text-gray-600 hover:bg-gray-200"
          }`}
          role="tab"
          aria-selected={activeTab === "details"}
        >
          <img 
            src={EmployeeTimeline} 
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span className={activeTab === "details" ? "font-semibold" : "font-medium"}>
            Employee Details & Timeline
          </span>
        </Button>

        <Button
          variant="ghost"
          onClick={() => setActiveTab("self")}
          className={`px-6 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
            activeTab === "self"
              ? "bg-purple-100 text-purple-800 shadow-md outline outline-2 outline-purple-800"
              : "text-gray-600 hover:bg-gray-200"
          }`}
          role="tab"
          aria-selected={activeTab === "self"}
        >
          <img 
            src={OkrReview} 
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span className={activeTab === "self" ? "font-semibold" : "font-medium"}>
            Self Ratings & OKRs
          </span>
        </Button>

        <Button
          variant="ghost"
          onClick={() => setActiveTab("team")}
          className={`px-6 py-3 rounded-lg font-medium flex items-center gap-3 transition-colors ${
            activeTab === "team"
              ? "bg-purple-100 text-purple-800 shadow-md outline outline-2 outline-purple-800"
              : "text-gray-600 hover:bg-gray-200"
          }`}
          role="tab"
          aria-selected={activeTab === "team"}
        >
          <img 
            src={TeamRating} 
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span className={activeTab === "team" ? "font-semibold" : "font-medium"}>
            Team Rating & Feedbacks
          </span>
        </Button>
      </div>
      <div>
        {activeTab === "details" && (
          <div className="min-h-[420px] md:h-[calc(100vh-220px)] md:overflow-hidden">
            <EmployeeDetailsTimeline
              timeline={effectiveTimeline}
              employee={effectiveEmployee}
            />
          </div>
        )}
        {activeTab === "self" && <SelfRatingsOKRs employeeId={params.employeeId} />}
        {activeTab === "team" && <TeamRatingsFeedbacks employeeId={params.employeeId} />}
      </div>
    </div>
  );
};

export default AppraisalAdminViewDetails;
